import { ReactNode } from 'react';
import { format } from 'date-fns';
import { useReduxState } from 'hooks';
import {
  Alert,
  Button,
  ConfirmButton,
  ConfirmDeleteButton,
  DisplayFormattedDate,
  MoreOptions
} from 'components/common';
import { Icon } from 'components/common/icons';

interface Props {
  children?: ReactNode;
  competencyId: number;
  description?: string;
  expirationDate?: string;
  firstGrantDate?: string;
  grantedByHigherLevel?: boolean;
  grantors?: ReactNode;
  id: number;
  isChecked?: boolean;
  isCheckedPending?: boolean;
  name: string;
  onDelete?: (
    competencyId: number,
    id: number,
    deletionReason?: string
  ) => Promise<boolean>;
  onDeleteConfirmWarning?: string;
  onRemove?: (competencyId: number, id: number) => Promise<void> | void;
}

const CompetenciesCardLevel = ({
  children,
  competencyId,
  description,
  expirationDate,
  firstGrantDate,
  grantedByHigherLevel,
  grantors,
  id,
  isChecked,
  isCheckedPending,
  name,
  onDelete,
  onDeleteConfirmWarning,
  onRemove
}: Props) => {
  const {
    messages,
    theme,
    serverConfig: { requireReasonWhenDeletingData }
  } = useReduxState();

  return (
    <div className="level">
      <div className="header">
        <div
          aria-label={
            isChecked ? messages.commonGranted : messages.commonNotGranted
          }
          className={`status ${isChecked ? 'status-checked' : ''} ${
            isCheckedPending ? 'status-checked-pending' : ''
          }`}
        >
          <Icon icon={isChecked || isCheckedPending ? 'check' : 'times'} />
        </div>

        <div className="content">
          <div className="title">
            {name}

            {description ? (
              <div
                className="description"
                aria-label={description}
                data-tooltip={description}
              >
                <Icon icon="question-circle" />
              </div>
            ) : null}
          </div>

          {grantors ? (
            <>
              {firstGrantDate ? (
                <div className="item">
                  <div className="item-label">{messages.commonGrantedOn} </div>
                  <div className="grant-date">
                    <time>{format(Date.parse(firstGrantDate), 'dd.MM.y')}</time>
                  </div>
                </div>
              ) : null}

              <div className="item">
                <div className="item-label">{messages.commonGrantedBy}</div>

                <div className="grantors">{grantors}</div>
              </div>
            </>
          ) : grantedByHigherLevel ? (
            <div className="item">
              <div className="item-label">
                {messages.competenciesGrantedBecauseHigherLevel}
              </div>
            </div>
          ) : null}

          {expirationDate ? (
            <div className="item">
              <div className="item-label">{messages.commonExpires}</div>

              <div className="expiration-date">
                <DisplayFormattedDate date={expirationDate} />
              </div>
            </div>
          ) : null}
        </div>

        {onDelete ? (
          <MoreOptions renderAsFixed={true} styleContentButtons={false}>
            {requireReasonWhenDeletingData ? (
              <ConfirmDeleteButton
                buttonIsDropdown={true}
                buttonText={messages.competenciesRemove}
                confirmText={`${
                  onDeleteConfirmWarning ? `${onDeleteConfirmWarning} ` : ''
                }${messages.competenciesRemoveConfirm}`}
                confirmTitle={messages.competenciesRemove}
                id={`deletionReasonUserCompetencyLevel-${competencyId}-${id}`}
                onConfirm={(values) =>
                  onDelete(competencyId, id, values?.deletionReason)
                }
                successMessage={messages.competenciesRemoveSuccess}
              />
            ) : (
              <ConfirmButton
                actionText={messages.commonRemove}
                buttonIsDropdown={true}
                buttonText={messages.competenciesRemove}
                category="danger"
                confirmText={
                  <>
                    {onDeleteConfirmWarning ? (
                      <Alert
                        type="warning-light"
                        style={{ marginBottom: 15 }}
                        text={onDeleteConfirmWarning}
                      />
                    ) : null}

                    {messages.competenciesRemoveConfirm}
                  </>
                }
                confirmTitle={messages.competenciesRemove}
                onConfirm={() => onDelete(competencyId, id)}
                successMessage={messages.competenciesRemoveSuccess}
              />
            )}
          </MoreOptions>
        ) : onRemove ? (
          <Button
            category="lighter"
            icon="trash-alt"
            onClick={() => onRemove(competencyId, id)}
            size="header"
            style={{ flexShrink: 0, margin: '-5px 0' }}
            tooltip={messages.commonRemove}
          />
        ) : null}
      </div>

      {children}

      <style jsx>{`
        .level {
          border-top: 1px solid ${theme.lightGreyDark};
          padding: 15px;
        }
        .header {
          display: flex;
          gap: 10px;
        }
        .content {
          flex-grow: 1;
        }
        .title {
          align-items: center;
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 5px;
        }
        .description {
          font-size: 1.4rem;
          position: relative;
          top: 1px;
        }
        .status {
          align-items: center;
          background-color: ${theme.lightGreyDark};
          border-radius: 20px;
          color: ${theme.textColorLight};
          display: flex;
          flex-shrink: 0;
          font-size: 1rem;
          justify-content: center;
          height: 20px;
          width: 20px;
        }
        .status-checked {
          background-color: ${theme.successColor};
          color: ${theme.successTextColor};
        }
        .status-checked-pending {
          background-color: ${theme.lightGreyDark};
          color: ${theme.textColorLight};
        }
        .item {
          display: flex;
          gap: 15px;
          margin-top: 10px;
        }
        .item-label {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 3px;
          white-space: nowrap;
        }
        .grantors {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 20px;
        }
        .expiration-date {
          margin-top: 3px;
        }
        .grant-date {
          margin-top: 3px;
          font-size: 1.4 rem;
        }
      `}</style>
    </div>
  );
};

export default CompetenciesCardLevel;
